/**
 * SUNBAY CPad Pay device form rules (Super Admin may name vendors).
 * A SUNBAY row is a pool row: name + terminal serial + where Tapro runs
 * relative to the till. No secret — the till authorises with the platform's
 * SUNBAY credentials and the LOCATION's merchant id.
 *   register → POST …/emv-devices { paymentConfigId:'sunbay_tapro', deviceName, serialNumber, sunbayConnection }
 */
export type SunbayConnection = 'sub_screen' | 'on_device' | 'cable_aoa' | 'cable_vsp';
export const SUNBAY_CLASS = 'sunbay_tapro';
export const SUNBAY_CONNECTIONS: ReadonlyArray<{ id: SunbayConnection; label: string }> = [
  { id: 'cable_vsp', label: 'USB cable — CPad Pay' },
  { id: 'cable_aoa', label: 'USB cable — handheld (P3H)' },
  { id: 'sub_screen', label: 'Docked in T3 (sub-screen)' },
  { id: 'on_device', label: 'Same device (app to app)' },
];

export interface SunbayDeviceForm { deviceName: string; serialNumber: string; connection: SunbayConnection }

export const emptySunbayForm = (): SunbayDeviceForm => ({ deviceName: '', serialNumber: '', connection: 'cable_vsp' });

export const isSunbayDevice = (d: any) => String(d?.deviceClass || '') === SUNBAY_CLASS;

export function sunbayFormFromDevice(d: any): SunbayDeviceForm {
  return {
    deviceName: d?.deviceName || '', serialNumber: d?.serialNumber || '',
    connection: ['on_device', 'cable_aoa', 'cable_vsp'].includes(d?.sunbayConnection) ? d.sunbayConnection : 'sub_screen',
  };
}

export const SERIAL_MESSAGE = 'Enter the terminal serial number from the CPad Pay';

/** First problem as copy, or null when valid. */
export function validateSunbayForm(f: SunbayDeviceForm): string | null {
  if (!f.serialNumber.trim()) return SERIAL_MESSAGE;
  if (!f.deviceName.trim()) return 'Enter a device name';
  return null;
}

export function buildSunbayDeviceBody(f: SunbayDeviceForm) {
  return {
    paymentConfigId: SUNBAY_CLASS as typeof SUNBAY_CLASS,
    deviceName: f.deviceName.trim(), serialNumber: f.serialNumber.trim(), sunbayConnection: f.connection,
  };
}

/**
 * A CPad Pay cabled to the till runs RubyTill Pay (customer screen + Tapro), so it is
 * registered as a RubyTill Pay device on the SUNBAY engine — which gets a pairing
 * code like every RubyTill Pay terminal. `on_device` / `cable_aoa` (the till drives
 * Tapro itself on a P3) stay plain `sunbay_tapro` rows.
 */
export const isCpadRtpayConnection = (c: SunbayConnection) => c === 'cable_vsp' || c === 'sub_screen';

export function buildCpadRtpayBody(f: SunbayDeviceForm) {
  return {
    paymentConfigId: 'dejavoo_rubytill_pay' as const,
    deviceName: f.deviceName.trim(), serialNumber: f.serialNumber.trim(),
    rtpayCardEngine: 'sunbay' as const, rtpayMode: 'customer_display' as const,
  };
}

/** Edit requests — changed fields only (PATCH …/emv-devices/{id}). */
export function sunbayEditPatches(device: any, f: SunbayDeviceForm): Array<Record<string, unknown>> {
  const base = sunbayFormFromDevice(device);
  const out: Array<Record<string, unknown>> = [];
  if (f.deviceName.trim() && f.deviceName.trim() !== base.deviceName) out.push({ deviceName: f.deviceName.trim() });
  if (f.serialNumber.trim() && f.serialNumber.trim() !== base.serialNumber) out.push({ deviceConfig: { serialNumber: f.serialNumber.trim() } });
  if (f.connection !== base.connection) out.push({ sunbayConnection: f.connection });
  return out;
}
